// homeEngine — pure Phase-A computations for the revamped home.
// No Vue, no Firebase: every function takes plain data and returns plain data,
// so the whole module is unit-testable (ENGINE_SPEC_V2 §1/§3, DATA_AUDIT §1).
// Phase C adds the drill scheduler beside these.

/** 노출 floor: a lesson is "meaningfully opened" past this much real playback. */
export const EXPOSURE_FLOOR_SEC = 10;

/** Rank ladder (spec §1, LOCKED). Thresholds are 복습한-표현 counts. */
export const RANK_LADDER = [
  { lv: 1, name: '스태프', threshold: 0 },
  { lv: 2, name: '어쏘', threshold: 10 },
  { lv: 3, name: '팀장', threshold: 30 },
  { lv: 4, name: '시니어 매니저', threshold: 60 },
  { lv: 5, name: '디렉터', threshold: 120 },
  { lv: 6, name: '파트너', threshold: 250 },
];

/** Greeting pools (spec §3, Eddie-authored — extend here only). */
// Ruled 2026-09-07: no 화이팅 register, no exclamation marks — a line with either is out.
//
// AND IT HAS TO FIT ON ONE LINE (Eddie, 2026-09-17). The greeting shares its
// row with the rank pill, so at 390px the text has about 244px, and the member's
// own name is prepended to whatever is written here. Measured across 320/360/390
// with two-syllable, three-syllable and romanized given names, the budget is
// TEN non-space characters including the leading `님,`. Eleven already breaks
// for a romanized name at 390.
//
// `님, 오늘은 어떤 표현을 복습할까요?` was sixteen and wrapped to two lines at every
// width with every name, three at 320 with a romanized name. Removed by Eddie's
// ruling. homeEngine.test.mjs holds the budget so the next line cannot repeat it.
export const GREETING_POOLS = {
  morning: ['님, 좋은 아침이에요'],
  day: ['님, 이어서 해볼까요?', '님, 점심은 드셨나요?'],
  evening: ['님, 👋'],
};

/**
 * The set of videoIds this member has meaningfully watched — the LOCKED 노출
 * rule (DATA_AUDIT §0): progress doc with watchedSecTotal ≥ floor, OR a
 * seeded doc, OR legacy membership in the video's watchedMembers.
 * @param {Object<string,object>} progressMap - videoId → masterclassProgress doc
 * @param {Array<{id:string, watchedMembers?:string[]}>} videos
 * @param {string} uid
 * @returns {Set<string>} watched videoIds
 */
export function watchedVideoIds(progressMap, videos, uid) {
  const watched = new Set();
  // An explicit member override ("I haven't really seen this") beats every
  // inference we make — including our own measured seconds. Someone who
  // played 10s by accident was not meaningfully exposed to the lesson, and
  // their knowledge of that beats ours. The seconds themselves are kept
  // intact for weekly minutes; only the 노출 claim is withdrawn.
  const optedOut = new Set();
  for (const [videoId, p] of Object.entries(progressMap || {})) {
    if (videoId === '_summary' || !p) continue;
    if (p.exposureOptOut) { optedOut.add(videoId); continue; }
    if ((p.watchedSecTotal || 0) >= EXPOSURE_FLOOR_SEC || p.seededFrom) watched.add(videoId);
  }
  for (const v of videos || []) {
    if (optedOut.has(v.id)) continue; // override also wins over legacy watchedMembers
    if (Array.isArray(v.watchedMembers) && v.watchedMembers.includes(uid)) watched.add(v.id);
  }
  return watched;
}

/**
 * `seededFrom` values that represent the MEMBER's own claim rather than ours.
 * `manual` is the 도장 stamp — they affirmed they watched it off-platform.
 * Everything else (`uniqueViewers`, `watchedMembers`, and their comma pair) is
 * a launch backfill inferred from legacy analytics: it means "some signal
 * touched this lesson", not "this member watched it".
 */
const SELF_ATTESTED_SEEDS = new Set(['manual']);

/**
 * The videoIds a member may be DRILLED on — a strictly narrower rule than
 * watchedVideoIds(), and deliberately not the same function.
 *
 * The display rule (watchedVideoIds) is generous on purpose: the launch
 * backfill is what makes a long-standing member's progress bar reflect the
 * year they already put in, and shrinking it would erase visible progress
 * nobody asked us to erase. The DRILL cannot afford that generosity. Being
 * asked to retrieve an expression from a lesson you never opened is not a
 * review, it is an unsolvable puzzle — reported from the field 2026-08, and
 * measured at the time as 158 of 185 seeded exposures among active drillers
 * coming from inferred legacy signals, two members drilling on lessons with
 * zero measured playback.
 *
 * So: measured playback, or the member's own stamp. Nothing inferred.
 * `exposureOptOut` still wins over both — an explicit withdrawal always does.
 *
 * @param {Object<string,object>} progressMap - videoId → masterclassProgress doc
 * @returns {Set<string>} videoIds eligible to supply drill cards
 */
export function drillEligibleVideoIds(progressMap) {
  const eligible = new Set();
  for (const [videoId, p] of Object.entries(progressMap || {})) {
    if (videoId === '_summary' || !p || p.exposureOptOut) continue;
    if ((p.watchedSecTotal || 0) >= EXPOSURE_FLOOR_SEC) { eligible.add(videoId); continue; }
    if (p.seededFrom && SELF_ATTESTED_SEEDS.has(String(p.seededFrom))) eligible.add(videoId);
  }
  return eligible;
}

/**
 * 노출 expressions: distinct published expressions taught by watched lessons.
 * @param {Set<string>} watchedIds - from watchedVideoIds() for display, or
 *   drillEligibleVideoIds() for the drill pool
 * @param {Array<{id:string, lessonKey?:string}>} videos
 * @param {Array<{recordId:string, masterclass_link?:Array<{id:string}>}>} expressions - mirror
 * @returns {Array<object>} the exposed expression records
 */
export function exposedExpressions(watchedIds, videos, expressions) {
  const watchedLessonKeys = new Set();
  for (const v of videos || []) {
    if (v.lessonKey && watchedIds.has(v.id)) watchedLessonKeys.add(v.lessonKey);
  }
  return (expressions || []).filter(
    (e) => Array.isArray(e.masterclass_link) && e.masterclass_link.some((l) => l && watchedLessonKeys.has(l.id))
  );
}

/** Minutes after start that a session still reads LIVE (one class length). */
export const SESSION_LIVE_MIN = 60;
/**
 * Minutes after start that the pill stays on the banner at all.
 *
 * Eddie rolls the session date forward to the following week BY HAND after
 * each class, so between the end of class and that edit the row would
 * otherwise go empty and read as "no office hour this week". This window is
 * the grace period for that manual step, not a claim the class is running.
 */
export const SESSION_PERSIST_MIN = 120;
/**
 * LIVE turns on this many minutes BEFORE the start time.
 *
 * This pair (lead + live length) is also the 참여-link window: the Zoom link is
 * live exactly while the pill says LIVE, and both sides read these constants so
 * they cannot drift apart again.
 */
export const SESSION_LIVE_LEAD_MIN = 5;

/** Local midnight for a timestamp, in the VIEWER's zone (setHours is local). */
function localDayStart(ms) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Whole calendar days between two instants in the viewer's local zone.
 *
 * NOT `(b - a) / 86400000`. Elapsed-hours math answers "how many 24-hour
 * blocks away", which is a different question from "how many sleeps away" and
 * disagrees with it for most of the day: a class at 20:00 today, read at
 * 08:00, is half a block away and rounds to 1 — rendering 내일 on the morning
 * OF the class. Rounding the difference of two local midnights is DST-safe
 * (a 23- or 25-hour day still rounds to exactly 1).
 */
export function calendarDayDiff(fromMs, toMs) {
  return Math.round((localDayStart(toMs) - localDayStart(fromMs)) / 86400000);
}

/**
 * The banner pill for one live session, or null when it should not render.
 *
 * Pure so the boundaries are testable without a browser or a fixed clock —
 * every rule below was a reported bug, and all three are date arithmetic that
 * looks right until you read it at the wrong hour of the day.
 *
 * `live` is the same window the 참여 link opens in, so a caller never has to
 * re-derive "can they join right now" and get a different answer.
 *
 * @param {number} sessionMs start time
 * @param {number} nowMs
 * @returns {{label:string, days:number, live:boolean, ended:boolean}|null}
 */
export function sessionPill(sessionMs, nowMs) {
  const diff = sessionMs - nowMs;
  const minsSinceStart = -diff / 60000;
  if (minsSinceStart > SESSION_PERSIST_MIN) return null;

  const days = calendarDayDiff(nowMs, sessionMs);

  // LIVE: from shortly before the start until one class length after it. This
  // is exactly when the 참여 link is open.
  if (diff <= SESSION_LIVE_LEAD_MIN * 60000 && minsSinceStart <= SESSION_LIVE_MIN) {
    return { label: 'LIVE', days, live: true, ended: false };
  }
  // Past the live window but inside the grace period: the class is over and the
  // link is closed, so say so. 오늘 would be true and useless here — it reads as
  // "still coming" to someone who just missed it, and it is the reason the pill
  // and the detail card disagreed (the card already said 종료).
  if (minsSinceStart > 0) {
    return days < 0 ? null : { label: '종료', days, live: false, ended: true };
  }

  if (days === 0) return { label: '오늘', days, live: false, ended: false };
  if (days === 1) return { label: '내일', days, live: false, ended: false };
  if (days <= 7) return { label: `D-${days}`, days, live: false, ended: false };
  const d = new Date(sessionMs);
  return { label: `${d.getMonth() + 1}/${d.getDate()}`, days, live: false, ended: false };
}

/**
 * Per-lane stats for the 마스터클래스 section (DATA_AUDIT §1e).
 * @param {Array<{key:string,label:string}>} lanes - MASTERCLASS_LANES
 * @param {Array<object>} videos - published lane videos (member-visible)
 * @param {Set<string>} watchedIds
 * @param {Array<object>} expressions - mirror
 * @returns {Array<{key,label,published,watched,remaining,exprCount,nextTitle,ringPct}>}
 */
export function laneStats(lanes, videos, watchedIds, expressions) {
  return (lanes || []).map((lane) => {
    const laneVideos = (videos || []).filter((v) => v.category === lane.label);
    const watched = laneVideos.filter((v) => watchedIds.has(v.id)).length;
    const laneKeys = new Set(laneVideos.map((v) => v.lessonKey).filter(Boolean));
    const exprCount = (expressions || []).filter(
      (e) => Array.isArray(e.masterclass_link) && e.masterclass_link.some((l) => l && laneKeys.has(l.id))
    ).length;
    const next = laneVideos.find((v) => !watchedIds.has(v.id)) || null;
    return {
      key: lane.key,
      label: lane.label,
      published: laneVideos.length,
      watched,
      remaining: laneVideos.length - watched,
      exprCount,
      nextTitle: next ? next.title : null,
      ringPct: laneVideos.length ? Math.round((watched / laneVideos.length) * 100) : 0,
    };
  });
}

/**
 * Rank for a 복습한-표현 count. Phase A: everyone is 0 → Lv.1 스태프 (honest;
 * Eddie-locked to SHOW pre-Phase-C, not hide).
 * @param {number} masteredCount
 * @returns {{lv:number,name:string,next:{lv,name,threshold}|null,remaining:number|null}}
 */
export function rankFor(masteredCount) {
  let current = RANK_LADDER[0];
  for (const r of RANK_LADDER) if (masteredCount >= r.threshold) current = r;
  const next = RANK_LADDER.find((r) => r.threshold > masteredCount) || null;
  return {
    lv: current.lv,
    name: current.name,
    next,
    remaining: next ? next.threshold - masteredCount : null,
  };
}

/**
 * Greeting line for an hour-of-day (Asia/Seoul), random within the bucket.
 * @param {number} hour 0-23
 * @param {() => number} [rng] injectable for tests
 * @returns {string} line WITHOUT the name (caller prepends name)
 */
export function greetingLine(hour, rng = Math.random) {
  const pool =
    hour >= 5 && hour < 11 ? GREETING_POOLS.morning
    : hour >= 11 && hour < 18 ? GREETING_POOLS.day
    : GREETING_POOLS.evening;
  return pool[Math.floor(rng() * pool.length)] || pool[0];
}
